import { useRef, useEffect } from 'react';

import { useMerchantsListQuery } from '~entities/merchant';
import { useGetPointsOfSalesQuery } from '~entities/pos';

import type { AbstractOption } from '~shared/types/abstract-options';

import {
  getUpToDateMerchantIds,
  getUpToDatePosIds,
  getUpToDateStatuses,
} from '../lib/get-up-to-date-filters';
import type { FiltersValue } from '../ui/filters/filters';

export function useSyncedInitialValue<T = string>({
  statusOptions,
  getInitialValue,
  setInitialValue,
}: {
  statusOptions?: AbstractOption<T>[];
  getInitialValue: () => FiltersValue<string>;
  setInitialValue: (value: FiltersValue<T>) => void;
}) {
  const { data: merchantsList, isSuccess: isMerchantsListSuccess } =
    useMerchantsListQuery();
  const { data: posList, isSuccess: isPosListSuccess } =
    useGetPointsOfSalesQuery();
  const initialValueSyncedRef = useRef<boolean>(false);

  // setInitialValue can update state of react component
  // setting state on render phase causes warning about incorrect setState, so added useEffect
  useEffect(() => {
    if (
      !initialValueSyncedRef.current &&
      isMerchantsListSuccess &&
      isPosListSuccess
    ) {
      initialValueSyncedRef.current = true;
      const savedValue = getInitialValue();

      setInitialValue({
        ...savedValue,
        statuses:
          statusOptions && savedValue.statuses
            ? getUpToDateStatuses(
                savedValue.statuses,
                statusOptions.map(({ value }) => value)
              )
            : undefined,
        merchantIds: savedValue.merchantIds
          ? getUpToDateMerchantIds(savedValue.merchantIds, merchantsList)
          : undefined,
        posIds: savedValue.posIds
          ? getUpToDatePosIds(savedValue.posIds, posList)
          : undefined,
      });
    }
  }, [
    isMerchantsListSuccess,
    isPosListSuccess,
    merchantsList,
    posList,
    getInitialValue,
    setInitialValue,
    statusOptions,
  ]);
}
